/**
 * Reports — Specification §17.
 *
 * The four reports share one period, because the question a manager actually
 * asks is "how did last month go", not "how did last month go for offices and
 * the quarter before that for people". Changing the period changes every
 * figure on the page at once, which is the only way the four tables can be
 * read against each other.
 *
 * Scope is the server's decision (RPT-005): an Admin's page covers every
 * office, a Manager's covers theirs. Nothing here asks for a wider set, and
 * asking would not work.
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { formatDuration, formatRate } from '@lto/contracts';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { Button, Empty, ErrorState, Metric, Panel, TableSkeleton } from '../components/ui';

type Granularity = 'day' | 'week' | 'month';

interface ConfirmationReport {
  readonly total: number;
  readonly sent: number;
  readonly received: number;
  readonly read: number;
  readonly replied: number;
  readonly resolved: number;
  readonly closed: number;
  readonly cancelled: number;
  readonly byStatus: Record<string, number>;
  readonly deliveryRate: number | null;
  readonly readRate: number | null;
  readonly responseRate: number | null;
  readonly closureRate: number | null;
}

interface OfficeRow {
  readonly officeId: string;
  readonly officeName: string;
  readonly officeCode: string;
  readonly total: number;
  readonly unread: number;
  readonly pending: number;
  readonly replied: number;
  readonly overdue: number;
  readonly averageResponseSeconds: number | null;
  readonly responseRate: number | null;
}

interface UserRow {
  readonly userId: string;
  readonly name: string;
  readonly officeName: string;
  readonly received: number;
  readonly replied: number;
  readonly unread: number;
  readonly awaitingReply: number;
  readonly averageReadSeconds: number | null;
  readonly averageReplySeconds: number | null;
  readonly responseRate: number | null;
}

interface ResponseTimeReport {
  readonly granularity: Granularity;
  readonly measured: number;
  readonly averageSeconds: number | null;
  readonly medianSeconds: number | null;
  readonly fastest: { confirmationId: string; referenceNo: string | null; subject: string; seconds: number } | null;
  readonly slowest: { confirmationId: string; referenceNo: string | null; subject: string; seconds: number } | null;
  readonly averageDeliverySeconds: number | null;
  readonly averageReadSeconds: number | null;
  readonly trend: ReadonlyArray<{ bucket: string; count: number; averageSeconds: number | null }>;
}

/** Presets, because nobody wants to type two dates to answer "how is this month". */
const PRESETS = [
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: '365d', label: 'Last 12 months', days: 365 },
  { key: 'all', label: 'All time', days: null },
] as const;

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

const th =
  'px-4 py-2 text-xs font-medium uppercase tracking-wider';

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th scope="col" className={`${th} ${align === 'right' ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
}

/**
 * The trend, as bars.
 *
 * Deliberately not a charting library: this is one series of one number, and
 * a dependency that ships a canvas renderer to draw twelve rectangles would be
 * a poor trade. Bars are scaled against the slowest period, and every bar
 * carries its figure as text so the shape is never the only information
 * (UI-007).
 */
function TrendBars({ points }: { points: ResponseTimeReport['trend'] }) {
  if (points.length === 0) {
    return (
      <Empty
        title="Nothing was answered in this period"
        detail="A trend needs at least one replied confirmation to plot."
      />
    );
  }

  const peak = Math.max(...points.map((p) => p.averageSeconds ?? 0), 1);

  return (
    <div className="space-y-1.5 px-4 py-3">
      {points.map((point, index) => {
        const seconds = point.averageSeconds ?? 0;
        const width = Math.max(2, Math.round((seconds / peak) * 100));
        return (
          <div key={point.bucket} className="flex items-center gap-3 text-xs">
            <span
              className="tabular w-24 shrink-0"
              style={{ color: 'var(--color-ink-subtle)' }}
            >
              {point.bucket}
            </span>
            <div
              className="h-4 flex-1 overflow-hidden"
              style={{ background: 'var(--color-canvas)', borderRadius: 'var(--radius-sm)' }}
            >
              <div
                className="h-full"
                style={{
                  width: `${width}%`,
                  background: 'var(--color-accent)',
                  borderRadius: 'var(--radius-sm)',
                  // Bars grow in sequence, briefly, so the eye follows the
                  // series left to right rather than being handed all of it.
                  transition: 'width var(--duration-slow) var(--ease-out)',
                  transitionDelay: `${Math.min(index * 30, 300)}ms`,
                }}
              />
            </div>
            <span className="tabular w-24 shrink-0 text-right font-medium">
              {formatDuration(point.averageSeconds)}
            </span>
            <span
              className="tabular w-16 shrink-0 text-right"
              style={{ color: 'var(--color-ink-subtle)' }}
            >
              {point.count} replied
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function Reports() {
  const { can, scopeFor } = useSession();
  const [preset, setPreset] = useState<(typeof PRESETS)[number]['key']>('30d');
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);

  const days = PRESETS.find((p) => p.key === preset)?.days ?? null;
  const from = days === null ? null : isoDay(new Date(Date.now() - days * 86_400_000));

  // One query string for every report, so the four tables always describe the
  // same period.
  const params = new URLSearchParams();
  if (from !== null) params.set('from', from);
  const range = params.toString();
  const withGranularity = new URLSearchParams(params);
  withGranularity.set('granularity', granularity);

  const suffix = range === '' ? '' : `?${range}`;
  // A Regular User holds report.view at OWN scope: there is nobody for them
  // to be compared against, and the server refuses the request. Asking the
  // scope rather than the permission keeps the panel off their page instead
  // of showing them a section that always errors.
  const canReportOnUsers = scopeFor('report.view') !== undefined && scopeFor('report.view') !== 'OWN';

  const confirmations = useQuery({
    queryKey: ['reports', 'confirmations', preset],
    queryFn: () => api.get<ConfirmationReport>(`/api/v1/reports/confirmations${suffix}`),
  });

  const offices = useQuery({
    queryKey: ['reports', 'offices', preset],
    queryFn: () => api.get<OfficeRow[]>(`/api/v1/reports/offices${suffix}`),
  });

  const users = useQuery({
    queryKey: ['reports', 'users', preset],
    queryFn: () => api.get<UserRow[]>(`/api/v1/reports/users${suffix}`),
    enabled: canReportOnUsers,
    retry: false,
  });

  const responseTimes = useQuery({
    queryKey: ['reports', 'response-times', preset, granularity],
    queryFn: () =>
      api.get<ResponseTimeReport>(`/api/v1/reports/response-times?${withGranularity.toString()}`),
  });

  async function exportCsv(key: string, path: string, filename: string) {
    setExportError(null);
    setExporting(key);
    try {
      const separator = path.includes('?') ? '&' : '?';
      await api.download(`${path}${separator}format=csv`, filename);
    } catch (error) {
      setExportError(
        error instanceof ApiError
          ? (error.problem?.detail ?? 'The export could not be produced.')
          : 'The export could not be produced.',
      );
    } finally {
      setExporting(null);
    }
  }

  function ExportButton({ id, path, filename }: { id: string; path: string; filename: string }) {
    if (!can('report.export')) return null;
    return (
      <Button loading={exporting === id} onClick={() => void exportCsv(id, path, filename)}>
        Export CSV
      </Button>
    );
  }

  const periodLabel = PRESETS.find((p) => p.key === preset)?.label ?? '';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Reports</h1>
          <p className="mt-0.5 text-sm" style={{ color: 'var(--color-ink-subtle)' }}>
            {periodLabel}, measured from when each confirmation was sent
          </p>
        </div>

        <div className="flex flex-wrap gap-1" role="group" aria-label="Reporting period">
          {PRESETS.map((option) => (
            <button
              key={option.key}
              onClick={() => setPreset(option.key)}
              aria-pressed={preset === option.key}
              className="interactive border px-2.5 py-1.5 text-sm font-medium"
              style={{
                borderRadius: 'var(--radius-sm)',
                background:
                  preset === option.key ? 'var(--color-accent-subtle)' : 'var(--color-surface)',
                borderColor:
                  preset === option.key ? 'var(--color-accent)' : 'var(--color-rule-strong)',
                color: preset === option.key ? 'var(--color-accent)' : 'var(--color-ink-muted)',
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {exportError !== null && (
        <div
          role="alert"
          className="animate-rise border px-3 py-2 text-sm"
          style={{
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-danger-subtle)',
            borderColor: 'var(--color-danger)',
            color: 'var(--color-danger)',
          }}
        >
          {exportError}
        </div>
      )}

      {/* ---- Confirmation report — RPT-001 ---- */}
      <Panel
        title="Confirmations"
        description="Milestones reached by everything sent in the period"
        actions={
          <ExportButton
            id="confirmations"
            path={`/api/v1/reports/confirmations${suffix}`}
            filename="confirmation-report.csv"
          />
        }
      >
        {confirmations.isPending && <TableSkeleton rows={2} columns={4} />}
        {confirmations.isError && (
          <ErrorState
            message="Could not load the confirmation report."
            onRetry={() => void confirmations.refetch()}
          />
        )}
        {confirmations.isSuccess && (
          <div className="space-y-3 px-4 py-3">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Metric label="Total" value={String(confirmations.data.total)} hint="Sent in the period" />
              <Metric
                label="Received"
                value={String(confirmations.data.received)}
                hint={`${formatRate(confirmations.data.deliveryRate)} of those sent`}
              />
              <Metric
                label="Read"
                value={String(confirmations.data.read)}
                hint={`${formatRate(confirmations.data.readRate)} of those received`}
              />
              <Metric
                label="Replied"
                value={String(confirmations.data.replied)}
                hint={`${formatRate(confirmations.data.responseRate)} response rate`}
              />
              <Metric label="Resolved" value={String(confirmations.data.resolved)} />
              <Metric
                label="Closed"
                value={String(confirmations.data.closed)}
                hint={`${formatRate(confirmations.data.closureRate)} closure rate`}
              />
              <Metric
                label="Cancelled"
                value={String(confirmations.data.cancelled)}
                hint="Excluded from every rate"
                tone={confirmations.data.cancelled > 0 ? 'attention' : 'neutral'}
              />
              <Metric
                label="Still open"
                value={String(
                  confirmations.data.total -
                    confirmations.data.closed -
                    confirmations.data.cancelled,
                )}
                tone="attention"
              />
            </div>

            <p className="text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
              These count milestones reached, not current status — a confirmation that was sent,
              read, answered and closed appears in all four. Where they stand right now:{' '}
              {Object.entries(confirmations.data.byStatus)
                .map(([status, count]) => `${status.toLowerCase()} ${count}`)
                .join(', ') || 'nothing in this period'}
              .
            </p>
          </div>
        )}
      </Panel>

      {/* ---- Response time report — RPT-004 ---- */}
      <Panel
        title="Response times"
        description="From the moment a confirmation is sent to its first reply"
        actions={
          <div className="flex items-center gap-2">
            <div className="flex gap-1" role="group" aria-label="Trend grouping">
              {(['day', 'week', 'month'] as const).map((option) => (
                <button
                  key={option}
                  onClick={() => setGranularity(option)}
                  aria-pressed={granularity === option}
                  className="interactive border px-2 py-1 text-xs font-medium capitalize"
                  style={{
                    borderRadius: 'var(--radius-sm)',
                    background:
                      granularity === option ? 'var(--color-accent-subtle)' : 'var(--color-surface)',
                    borderColor:
                      granularity === option ? 'var(--color-accent)' : 'var(--color-rule-strong)',
                    color: granularity === option ? 'var(--color-accent)' : 'var(--color-ink-muted)',
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
            <ExportButton
              id="response-times"
              path={`/api/v1/reports/response-times?${withGranularity.toString()}`}
              filename="response-times.csv"
            />
          </div>
        }
      >
        {responseTimes.isPending && <TableSkeleton rows={4} columns={3} />}
        {responseTimes.isError && (
          <ErrorState
            message="Could not load response times."
            onRetry={() => void responseTimes.refetch()}
          />
        )}
        {responseTimes.isSuccess && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 px-4 pt-3 lg:grid-cols-4">
              <Metric
                label="Average"
                value={formatDuration(responseTimes.data.averageSeconds)}
                hint={`Across ${responseTimes.data.measured} replied`}
              />
              <Metric
                label="Median"
                value={formatDuration(responseTimes.data.medianSeconds)}
                hint="Half were faster than this"
              />
              <Metric
                label="Time to deliver"
                value={formatDuration(responseTimes.data.averageDeliverySeconds)}
                hint="Sent to received by office"
              />
              <Metric
                label="Time to open"
                value={formatDuration(responseTimes.data.averageReadSeconds)}
                hint="Received to first opened"
              />
            </div>

            {(responseTimes.data.fastest !== null || responseTimes.data.slowest !== null) && (
              <div className="grid gap-3 px-4 sm:grid-cols-2">
                {(
                  [
                    ['Fastest reply', responseTimes.data.fastest, 'var(--color-status-resolved)'],
                    ['Slowest reply', responseTimes.data.slowest, 'var(--color-warning)'],
                  ] as const
                ).map(([label, extreme, color]) =>
                  extreme === null ? null : (
                    <Link
                      key={label}
                      to={`/confirmations/${extreme.confirmationId}`}
                      className="row-hover interactive block border px-3 py-2"
                      style={{
                        borderRadius: 'var(--radius-sm)',
                        borderColor: 'var(--color-rule)',
                        background: 'var(--color-surface)',
                      }}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-ink-subtle)' }}>
                          {label}
                        </span>
                        <span className="tabular text-sm font-semibold" style={{ color }}>
                          {formatDuration(extreme.seconds)}
                        </span>
                      </div>
                      <div className="reference mt-0.5 text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
                        {extreme.referenceNo ?? '—'}
                      </div>
                      <div className="truncate text-sm">{extreme.subject}</div>
                    </Link>
                  ),
                )}
              </div>
            )}

            <div className="border-t pt-1" style={{ borderColor: 'var(--color-rule)' }}>
              <TrendBars points={responseTimes.data.trend} />
            </div>
          </div>
        )}
      </Panel>

      {/* ---- Office report — RPT-002 ---- */}
      <Panel
        title="Offices"
        description="Compared on what each was sent"
        actions={
          <ExportButton
            id="offices"
            path={`/api/v1/reports/offices${suffix}`}
            filename="office-report.csv"
          />
        }
      >
        {offices.isPending && <TableSkeleton rows={4} columns={7} />}
        {offices.isError && (
          <ErrorState message="Could not load the office report." onRetry={() => void offices.refetch()} />
        )}
        {offices.isSuccess && offices.data.length === 0 && <Empty title="No offices to compare" />}
        {offices.isSuccess && offices.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead className="sticky-head">
                <tr
                  className="border-b"
                  style={{ borderColor: 'var(--color-rule)', color: 'var(--color-ink-subtle)' }}
                >
                  <Th>Office</Th>
                  <Th align="right">Volume</Th>
                  <Th align="right">Unopened</Th>
                  <Th align="right">Pending</Th>
                  <Th align="right">Overdue</Th>
                  <Th align="right">Avg response</Th>
                  <Th align="right">Response rate</Th>
                </tr>
              </thead>
              <tbody className="stagger">
                {offices.data.map((row) => (
                  <tr
                    key={row.officeId}
                    className="row-hover border-b last:border-b-0"
                    style={{ borderColor: 'var(--color-rule)' }}
                  >
                    <td className="px-4 py-2">
                      <div className="font-medium">{row.officeName}</div>
                      <div className="reference text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
                        {row.officeCode}
                      </div>
                    </td>
                    <td className="tabular px-4 py-2 text-right">{row.total}</td>
                    <td className="tabular px-4 py-2 text-right">{row.unread}</td>
                    <td className="tabular px-4 py-2 text-right">{row.pending}</td>
                    <td
                      className="tabular px-4 py-2 text-right font-medium"
                      style={{ color: row.overdue > 0 ? 'var(--color-danger)' : undefined }}
                    >
                      {row.overdue}
                    </td>
                    <td className="tabular px-4 py-2 text-right">
                      {formatDuration(row.averageResponseSeconds)}
                    </td>
                    <td className="tabular px-4 py-2 text-right">{formatRate(row.responseRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* ---- User report — RPT-003 ---- */}
      {canReportOnUsers && (
        <Panel
          title="People"
          description="Compared on what each has handled"
          actions={
            <ExportButton
              id="users"
              path={`/api/v1/reports/users${suffix}`}
              filename="user-report.csv"
            />
          }
        >
          {users.isPending && <TableSkeleton rows={5} columns={7} />}
          {users.isError && (
            <ErrorState
              message={
                users.error instanceof ApiError && users.error.status === 403
                  ? 'Your role does not include reporting on other people.'
                  : 'Could not load the people report.'
              }
              {...(users.error instanceof ApiError && users.error.status === 403
                ? {}
                : { onRetry: () => void users.refetch() })}
            />
          )}
          {users.isSuccess && users.data.length === 0 && <Empty title="Nobody to compare" />}
          {users.isSuccess && users.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead className="sticky-head">
                  <tr
                    className="border-b"
                    style={{ borderColor: 'var(--color-rule)', color: 'var(--color-ink-subtle)' }}
                  >
                    <Th>Name</Th>
                    <Th>Office</Th>
                    <Th align="right">Handled</Th>
                    <Th align="right">Replied</Th>
                    <Th align="right">Awaiting</Th>
                    <Th align="right">Avg to open</Th>
                    <Th align="right">Avg to reply</Th>
                    <Th align="right">Response rate</Th>
                  </tr>
                </thead>
                <tbody className="stagger">
                  {users.data.map((row) => (
                    <tr
                      key={row.userId}
                      className="row-hover border-b last:border-b-0"
                      style={{ borderColor: 'var(--color-rule)' }}
                    >
                      <td className="px-4 py-2 font-medium">{row.name}</td>
                      <td className="px-4 py-2" style={{ color: 'var(--color-ink-muted)' }}>
                        {row.officeName}
                      </td>
                      <td className="tabular px-4 py-2 text-right">{row.received}</td>
                      <td className="tabular px-4 py-2 text-right">{row.replied}</td>
                      <td
                        className="tabular px-4 py-2 text-right"
                        style={{ color: row.awaitingReply > 0 ? 'var(--color-warning)' : undefined }}
                      >
                        {row.awaitingReply}
                      </td>
                      <td className="tabular px-4 py-2 text-right">
                        {formatDuration(row.averageReadSeconds)}
                      </td>
                      <td className="tabular px-4 py-2 text-right">
                        {formatDuration(row.averageReplySeconds)}
                      </td>
                      <td className="tabular px-4 py-2 text-right">{formatRate(row.responseRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
